// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Button,
  Dropdown,
  InputLabel,
  Radio,
  TextArea,
  TextInput,
  useToast,
} from '@loa/ui-components';
import type { Client } from '../../../types/client';
import type { Quote, QuoteProvider } from '../../../types/quote';
import type {
  CreatePensionWizardRequest,
  PensionWizardBootstrapResult,
} from '../../../types/proposal';
import { brokerageConfigApi } from '../../../api/brokerageConfigApi';
import { proposalApi } from '../../../api/proposalApi';
import { quoteApi } from '../../../api/quoteApi';
import { useSoloClient } from '../../../hooks/useSoloClient';
import { useProductCatalogue } from '../../../hooks/useProductCatalogue';
import { ProviderMultiSelect } from '../../quotes/ProviderMultiSelect';

const NRA_MIN = 50;
const NRA_MAX = 75;

const parseAmount = (raw: string): number => {
  const cleaned = raw.replace(/[^0-9.,-]/g, '').replace(/,/g, '');
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
};

const formatDate = (iso: string | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-GB');
};

export type WizardStepQuoteRequestProps = Readonly<{
  profileId: string;
  brokerageId: string;
  clients: Client[];
  /**
   * When provided, the form renders in edit mode against this quote — the
   * target client is fixed (a wizard proposal's target client cannot change
   * after bootstrap, PRD FR-7) and submit calls {@code quoteApi.updateQuote}
   * instead of bootstrapping a new wizard.
   */
  existingQuote?: Quote;
  /** The proposal's target client id, used to pre-select the radio in edit mode. */
  targetClientId?: string;
  onBootstrapped: (result: PensionWizardBootstrapResult) => void;
  onQuoteUpdated?: (quote: Quote) => void;
  onCancel: () => void;
}>;

/**
 * Step 1 of the pension proposal wizard (Figma 1453-62405): the quote
 * request form. On submit it bootstraps the Quote (existing quote engine)
 * and the DRAFT proposal shell in one backend transaction
 * (`POST /api/proposals/pension-wizard`), or — in edit mode — updates the
 * already-bootstrapped quote via the existing `PUT /api/quotes/:id`.
 *
 * The client's date of birth is read-only, sourced from the selected
 * client's profile; per PRD RQ-6 it is never sent in the bootstrap request.
 */
export function WizardStepQuoteRequest({
  profileId,
  brokerageId,
  clients,
  existingQuote,
  targetClientId: targetClientIdProp,
  onBootstrapped,
  onQuoteUpdated,
  onCancel,
}: WizardStepQuoteRequestProps) {
  const { showToast } = useToast();
  const isEditMode = Boolean(existingQuote);
  const { isSolo, soloClient } = useSoloClient(clients);
  const { options: pensionTypeOptions } = useProductCatalogue('PENSION');

  const [targetClientId, setTargetClientId] = useState(targetClientIdProp ?? '');
  const [pensionType, setPensionType] = useState(existingQuote?.pensionType ?? '');
  const [amount, setAmount] = useState(existingQuote ? String(existingQuote.amount) : '');
  const [providerOptions, setProviderOptions] = useState<QuoteProvider[]>([]);
  const [selectedProviderIds, setSelectedProviderIds] = useState<string[]>(
    existingQuote?.sentToProviders.map((p) => p.id) ?? [],
  );
  const [normalRetirementAge, setNormalRetirementAge] = useState(
    existingQuote?.normalRetirementAge != null ? String(existingQuote.normalRetirementAge) : '',
  );
  const [specialRequests, setSpecialRequests] = useState(existingQuote?.specialRequests ?? '');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!brokerageId) return;
    let cancelled = false;
    brokerageConfigApi
      .getEnabledProviders(brokerageId)
      .then((data) => {
        if (cancelled) return;
        setProviderOptions(
          data.content.map((bp) => ({
            id: bp.provider.id,
            name: bp.provider.name,
            logoUrl: bp.provider.logoUrl,
          })),
        );
      })
      .catch(() => {
        if (!cancelled) setProviderOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [brokerageId]);

  // Solo relationships preselect the only possible recipient (Investment &
  // Protection Proposals PRD RQ-9) so the read-only DOB (RQ-6) populates
  // without an extra click, and the selector below is hidden entirely.
  useEffect(() => {
    if (!targetClientId && isSolo && soloClient) {
      setTargetClientId(soloClient.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSolo, soloClient]);

  const selectedClient = useMemo(
    () => clients.find((c) => c.id === targetClientId),
    [clients, targetClientId],
  );

  const clientDobDisplay = formatDate(selectedClient?.dateOfBirth ?? existingQuote?.clientDob);

  const nraNumber = Number(normalRetirementAge);
  const nraValid =
    Number.isFinite(nraNumber) && nraNumber >= NRA_MIN && nraNumber <= NRA_MAX;
  const amountValid = parseAmount(amount) > 0;

  const canSubmit =
    Boolean(targetClientId) &&
    Boolean(pensionType) &&
    amountValid &&
    selectedProviderIds.length >= 1 &&
    nraValid;

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      if (isEditMode && existingQuote) {
        const updated = await quoteApi.updateQuote(existingQuote.id, {
          clientNames: existingQuote.clientNames,
          amount: parseAmount(amount),
          currencyCode: existingQuote.currencyCode,
          clientDob: existingQuote.clientDob,
          pensionType: pensionType as Quote['pensionType'],
          normalRetirementAge: nraNumber,
          specialRequests: specialRequests.trim() || undefined,
          providerIds: selectedProviderIds,
        });
        showToast('Quote request updated', 'success');
        onQuoteUpdated?.(updated);
        return;
      }

      const request: CreatePensionWizardRequest = {
        profileId,
        targetClientId,
        pensionType,
        amount: parseAmount(amount),
        currencyCode: 'EUR',
        providerIds: selectedProviderIds,
        normalRetirementAge: nraNumber,
        specialRequests: specialRequests.trim() || undefined,
      };
      const result = await proposalApi.startPensionWizard(request);
      showToast('Quote requested', 'success');
      onBootstrapped(result);
    } catch (err: unknown) {
      const fallback = isEditMode ? 'Failed to update quote request' : 'Failed to request quotes';
      const message = axios.isAxiosError(err)
        ? (err.response?.data as { message?: string } | undefined)?.message ?? fallback
        : fallback;
      showToast(message, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pension-wizard-step" data-testid="wizard-step-quote-request">
      <h2 className="pension-wizard-step__title">New Pension Quote</h2>
      <div className="flex flex-col gap-lg">
        {!isSolo && (
          <div className="flex flex-col gap-md">
            <InputLabel required>Who is the quote for?</InputLabel>
            <div className="flex flex-col gap-sm">
              {clients.map((client) => (
                <Radio
                  key={client.id}
                  name="wizard-target-client"
                  label={`${client.firstName} ${client.lastName}`}
                  checked={targetClientId === client.id}
                  disabled={isEditMode}
                  onChange={() => setTargetClientId(client.id)}
                />
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-lg gap-y-lg">
          <Dropdown
            label="Pension Type"
            placeholder="Select a pension type"
            options={pensionTypeOptions}
            value={pensionType}
            onChange={(v) => setPensionType(v)}
            required
            fluid
          />

          <TextInput
            label="Amount"
            placeholder="€0"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            required
            fluid
          />

          <ProviderMultiSelect
            label="Request quote from"
            options={providerOptions}
            selectedIds={selectedProviderIds}
            onChange={setSelectedProviderIds}
          />

          <TextInput
            label="Client DOB"
            value={clientDobDisplay}
            disabled
            readOnly
            fluid
          />

          <TextInput
            label="Normal Retirement Age (NRA)"
            placeholder="65"
            value={normalRetirementAge}
            onChange={(e) => setNormalRetirementAge(e.target.value)}
            error={
              normalRetirementAge.trim().length > 0 && !nraValid
                ? `NRA must be between ${NRA_MIN} and ${NRA_MAX}`
                : undefined
            }
            required
            fluid
          />

          <TextArea
            label="Special requests"
            placeholder="e.g. NIL exit penalties; client recently relocated; advisor notes…"
            value={specialRequests}
            onChange={(e) => setSpecialRequests(e.target.value)}
            rows={4}
            fluid
          />
        </div>
      </div>

      <div className="pension-wizard-step__footer">
        <Button variant="outlined" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="filled"
          onClick={handleSubmit}
          disabled={!canSubmit || submitting}
          loading={submitting}
        >
          {isEditMode ? 'Save Changes' : 'Get Quotes'}
        </Button>
      </div>
    </div>
  );
}

export default WizardStepQuoteRequest;
